(() => {
  try {
    initPickupAvailability();
  } catch (error) {
    console.warn('Pickup availability failed safely', error);
  }

  function initPickupAvailability() {
    if (!customElements.get('pickup-availability')) {
      customElements.define(
        'pickup-availability',
        class PickupAvailability extends HTMLElement {
          constructor() {
            super();

            this.onClickOpenDrawer = this.onClickOpenDrawer.bind(this);
            this.requestId = 0;

            if (!this.hasAttribute('available')) {
              this.clearPickupUI();
              return;
            }

            this.fetchAvailability(this.dataset.variantId);
          }

          safeWarn(label, error) {
            console.warn(`Pickup availability safe fail: ${label}`, error);
          }

          clearPickupUI() {
            const button = this.querySelector('button');
            if (button && button.removeEventListener) {
              button.removeEventListener('click', this.onClickOpenDrawer);
            }

            this.innerHTML = '';
            this.removeAttribute('available');
          }

          async fetchAvailability(variantId) {
            if (!variantId) {
              this.clearPickupUI();
              return;
            }

            this.requestId += 1;
            const currentRequestId = this.requestId;

            let rootUrl = this.dataset.rootUrl || window?.Shopify?.routes?.root || '/';
            if (!rootUrl.endsWith('/')) {
              rootUrl = `${rootUrl}/`;
            }
            const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;
            const canAbort = typeof AbortController !== 'undefined';
            const controller = canAbort ? new AbortController() : null;
            const timeoutId = setTimeout(() => {
              try {
                controller?.abort();
              } catch (_) {
                return;
              }
            }, 7000);
            let renderSucceeded = false;

            try {
              const response = await fetch(
                variantSectionUrl,
                controller ? { signal: controller.signal } : undefined
              );
              if (!response || !response.ok) return;

              let text;
              try {
                text = await response.text();
              } catch {
                return;
              }

              if (!text || typeof text !== 'string') return;

              let doc;
              try {
                const parser = new DOMParser();
                doc = parser.parseFromString(text, 'text/html');
              } catch {
                return;
              }

              if (!doc || !doc.querySelector) return;
              if (currentRequestId !== this.requestId) return;

              const sectionInnerHTML = doc.querySelector('.shopify-section');
              if (!sectionInnerHTML) return;

              renderSucceeded = this.renderPreview(sectionInnerHTML);
            } catch (error) {
              if (error?.name !== 'AbortError') {
                this.safeWarn('fetchAvailability', error);
              }
            } finally {
              clearTimeout(timeoutId);

              if (currentRequestId !== this.requestId) return;

              if (!renderSucceeded) {
                this.clearPickupUI();
              }
            }
          }

          onClickOpenDrawer(evt) {
            try {
              const drawer = document.querySelector('pickup-availability-drawer');
              if (drawer && typeof drawer.show === 'function') {
                drawer.show(evt?.currentTarget || evt?.target || null);
              }
            } catch (error) {
              this.safeWarn('openDrawer', error);
            }
          }

          update(variant) {
            try {
              if (variant?.available) {
                this.dataset.variantId = String(variant.id || '');
                this.fetchAvailability(variant.id);
              } else {
                this.clearPickupUI();
              }
            } catch (error) {
              this.safeWarn('update', error);
              this.clearPickupUI();
            }
          }

          renderError() {
            return;
          }

          renderPreview(sectionInnerHTML) {
            try {
              if (!sectionInnerHTML || !sectionInnerHTML.querySelector) {
                return false;
              }

              const existingDrawer = document.querySelector('pickup-availability-drawer');
              if (existingDrawer && existingDrawer.remove) {
                existingDrawer.remove();
              }

              const preview = sectionInnerHTML.querySelector('pickup-availability-preview');
              if (!preview || !preview.outerHTML) {
                return false;
              }

              this.innerHTML = preview.outerHTML;
              this.setAttribute('available', '');

              const drawerElement = sectionInnerHTML.querySelector('pickup-availability-drawer');
              if (drawerElement && document?.body?.appendChild) {
                document.body.appendChild(drawerElement);

                const colorClassesToApply = (this.dataset.productPageColorScheme || '').split(' ').filter(Boolean);
                colorClassesToApply.forEach((colorClass) => {
                  if (drawerElement.classList && drawerElement.classList.add) {
                    drawerElement.classList.add(colorClass);
                  }
                });
              }

              const button = this.querySelector('button');
              if (button && button.addEventListener) {
                button.removeEventListener('click', this.onClickOpenDrawer);
                button.addEventListener('click', this.onClickOpenDrawer);
              }
              return true;
            } catch (error) {
              this.safeWarn('renderPreview', error);
              return false;
            }
          }
        }
      );
    }

    if (!customElements.get('pickup-availability-drawer')) {
      customElements.define(
        'pickup-availability-drawer',
        class PickupAvailabilityDrawer extends HTMLElement {
          constructor() {
            super();

            this.onBodyClick = this.handleBodyClick.bind(this);

            const closeButton = this.querySelector('button');
            if (closeButton && closeButton.addEventListener) {
              closeButton.addEventListener('click', () => {
                this.hide();
              });
            }

            this.addEventListener('keyup', (event) => {
              if (event?.code?.toUpperCase() === 'ESCAPE') this.hide();
            });
          }

          handleBodyClick(evt) {
            const target = evt?.target;
            if (
              target !== this &&
              !target?.closest?.('pickup-availability-drawer') &&
              target?.id !== 'ShowPickupAvailabilityDrawer'
            ) {
              this.hide();
            }
          }

          hide() {
            this.removeAttribute('open');
            document?.body?.removeEventListener?.('click', this.onBodyClick);
            document?.body?.classList?.remove?.('overflow-hidden');
            if (typeof removeTrapFocus === 'function') {
              removeTrapFocus(this.focusElement);
            }
          }

          show(focusElement) {
            this.focusElement = focusElement;
            this.setAttribute('open', '');
            document?.body?.addEventListener?.('click', this.onBodyClick);
            document?.body?.classList?.add?.('overflow-hidden');
            if (typeof trapFocus === 'function') {
              trapFocus(this);
            }
          }
        }
      );
    }
  }
})();
